import { useNavigate } from 'react-router-dom';
import { useTenant } from '../config/tenantConfig';
import { getMenuForTenant } from '../data/menus';
import TenantBrand from './TenantBrand';
import WineGlassIcon from './icons/WineGlassIcon';
import GrapeIcon from './icons/GrapeIcon';
import DollarIcon from './icons/DollarIcon';
import GlobeIcon from './icons/GlobeIcon';
import UtensilsIcon from './icons/UtensilsIcon';
import MenuBoardIcon from './icons/MenuBoardIcon';

const secondaryOptions = [
  {
    id: 'pairing',
    title: 'PELO PRATO',
    icon: <UtensilsIcon />,
    path: '/foods',
  },
  {
    id: 'price',
    title: 'PELO PREÇO',
    icon: <DollarIcon />,
    path: '/wines/price',
  },
  {
    id: 'grape',
    title: 'PELA UVA',
    icon: <GrapeIcon />,
    path: '/wines/grape',
  },
  {
    id: 'region',
    title: 'PELA ORIGEM',
    icon: <GlobeIcon />,
    path: '/regions',
  },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const tenant = useTenant();
  const isDefaultTenant = tenant.id === 'default';
  const menu = getMenuForTenant(tenant.id);
  const hasMenu = Boolean(menu);

  return (
    <div className="pwa-dashboard-landscape min-h-full bg-gradient-to-br from-wine-dark via-[#1e1a2e] to-wine-dark flex items-center justify-center px-4 py-3 tablet-landscape:py-2">
      <div className="w-full max-w-5xl mx-auto tablet-landscape:max-w-6xl">
        <div className="pwa-dashboard-card bg-gradient-to-br from-[#1a1625] to-[#24202f] rounded-[2.5rem] shadow-2xl p-5 md:p-8 lg:p-12 tablet-landscape:p-6 border border-gray-800/50">
          <div className="pwa-dashboard-title text-center mb-6 md:mb-8 lg:mb-10 tablet-landscape:mb-4">
            <div className="pwa-dashboard-logo flex justify-center mb-3 tablet-landscape:mb-2">
              <TenantBrand
                tenant={tenant}
                imageClassName="h-16 md:h-24 w-auto max-w-[280px] object-contain"
                textClassName="font-serif text-wine-gold text-3xl md:text-4xl font-semibold"
                fallback={<WineGlassIcon />}
              />
            </div>
            <h1 className="font-serif text-wine-gold text-2xl md:text-4xl lg:text-5xl tablet-landscape:text-3xl font-semibold tracking-wide mb-4 md:mb-6 lg:mb-8 tablet-landscape:mb-3">
              {isDefaultTenant ? (
                <>
                  Sommelier<br />Agora
                </>
              ) : (
                tenant.name
              )}
            </h1>
            <h2 className="text-white text-xl md:text-3xl lg:text-4xl tablet-landscape:text-2xl font-serif font-light italic leading-relaxed mb-2 tablet-landscape:mb-0">
              <span className="block">Cada vinho conta uma história.</span>
              <span className="block text-wine-gold font-normal mt-2 md:mt-3 tablet-landscape:mt-1">
                Qual será a sua?
              </span>
            </h2>
          </div>

          <div className="max-w-3xl mx-auto tablet-landscape:max-w-4xl space-y-3 md:space-y-4 tablet-landscape:space-y-3">
            {hasMenu && (
              <button
                type="button"
                onClick={() => navigate('/menu')}
                className="group w-full relative overflow-hidden bg-wine-gold/15 border-2 border-wine-gold rounded-2xl md:rounded-3xl py-5 md:py-7 lg:py-8 tablet-landscape:py-5 px-5 md:px-8 flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-5 transition-all duration-300 hover:bg-wine-gold/25 hover:scale-[1.02] hover:shadow-lg hover:shadow-wine-gold/25 active:scale-[0.98]"
              >
                <div className="flex-shrink-0 text-wine-gold transition-transform duration-300 group-hover:scale-110 scale-110 md:scale-125">
                  <MenuBoardIcon />
                </div>
                <div className="text-center sm:text-left">
                  <span className="block text-white text-lg md:text-2xl lg:text-3xl font-serif tracking-wide">
                    Pelo cardápio
                  </span>
                  <span className="block text-wine-gold/90 text-sm md:text-base mt-1 font-light">
                    O corte define o vinho — comece por aqui
                  </span>
                </div>
              </button>
            )}

            <p
              className={`text-center text-gray-500 text-xs md:text-sm uppercase tracking-widest ${
                hasMenu ? 'pt-1' : 'sr-only'
              }`}
            >
              {hasMenu ? 'Ou explore de outro jeito' : ''}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 tablet-landscape:grid-cols-2 gap-2 md:gap-3 tablet-landscape:gap-2.5">
              {secondaryOptions.map((option) => (
                <button
                  type="button"
                  key={option.id}
                  onClick={() => navigate(option.path)}
                  className={`group relative overflow-hidden bg-transparent border border-wine-gold/50 rounded-xl md:rounded-2xl flex items-center justify-start gap-3 transition-all duration-300 hover:bg-wine-gold/10 hover:border-wine-gold hover:scale-[1.02] active:scale-[0.98] ${
                    hasMenu
                      ? 'py-2.5 md:py-3.5 tablet-landscape:py-2.5 px-4 md:px-5'
                      : 'border-2 border-wine-gold rounded-2xl md:rounded-3xl py-3.5 md:py-5 lg:py-6 tablet-landscape:py-4 px-5 md:px-7 lg:px-8 gap-3.5 md:gap-5 lg:gap-6 hover:scale-105 hover:shadow-lg hover:shadow-wine-gold/20'
                  }`}
                >
                  <div
                    className={`flex-shrink-0 text-wine-gold transition-transform duration-300 group-hover:scale-110 ${
                      hasMenu ? 'scale-90 opacity-90' : ''
                    }`}
                  >
                    {option.icon}
                  </div>
                  <span
                    className={`text-white font-normal tracking-wide uppercase ${
                      hasMenu
                        ? 'text-xs md:text-sm lg:text-base text-gray-200'
                        : 'text-sm md:text-lg lg:text-xl'
                    }`}
                  >
                    {option.title}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] right-[calc(1rem+env(safe-area-inset-right,0px))] tablet-landscape:bottom-4 tablet-landscape:right-4 pointer-events-none">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="#C9A961" fillOpacity="0.6"/>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
